import React, {useEffect, useReducer, useRef, useState} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import FeatureName from "../../visComponents/featureName";
import AllFeaturesChecker from "../../visComponents/allFeaturesChecker";
import {EditIcon} from "../../common/icons";
import {getCurrentLayout} from "../../appLayout";
import useDebounce, {useOutsideClick} from "../../../misc/helperHooks";
import sqftShapeManager from "../../sqft/components/canvas/store/sqftShapeManager";
import {getNavItemsFromTheWizard} from "../../sqft/helpers/sqftHelperUi";
import {prepareSQFTDataCore} from "../../sqft/components/canvas/core/workspace/workspaceDataMapper";
import {
  isQuoteByQTCatNameEqualToCurrentQuote,
  loadQuoteByQtCatName,
  saveSQFTDataToQuote
} from "../../sqft/helpers/sqftQuotesHelper";
import workspaceControllers from "../../sqft/components/canvas/store/workspaceControllers";
import {CABINET_COMMON_COLOR_PART_IDS, HANDLE_TO_CABINETS, SQFT_FEATURES} from "../../sqft/constants";
import workspace from "../../sqft/components/canvas/core/workspace";
import {searchProductRequest} from "../../../api/search-api";
import {clearCacheForTheEdgeFeatures} from "../../../actions/sqftMenuFunctions";
import {setWizardConflictSteps, setWizardSelectedValues} from "../../../actions/wizardFunctions";
import {setSQFTCartItemsToAdd} from "../../../actions/sqftResourceFunctions";
import {createModalFixSnapshot} from "../../sqft/components/popup/modalFix/CompatibilityWarnings/modalFixHelper";
import {storeSnapshot} from "../../../actions/sqftModalFunctions";
import {findSlabConflict} from "../../sqft/components/canvas/core/slab/slabHelper";
import {is3DMode} from "../../sqft/components/navigation/NavigationHelper";
import {reloadShapesIn3DMode} from "../../sqft/helpers/3dHelper/modelsHelper";
import {canOpenWizardConflictPopup} from "../../../misc/conflictPopups";

const filterNavItemsWithSearch = (navItems, productText) => {
  if (!productText) {
    return navItems
  }
  const searchString = productText.toLowerCase();
  return navItems.filter(navItem => {
    const name = navItem && navItem.part && navItem.part.getFeatureName ? navItem.part.getFeatureName() : "";
    return name && name.toLowerCase().includes(searchString)
  })
}

const getChangedCabinetShapes = (sqftFeature, feature) => {
  if (!feature || !CABINET_COMMON_COLOR_PART_IDS.includes(feature.partId)) {
    return [];
  }

  const shapeIds = sqftFeature.shouldUseRelatedParentFeatureAsMain
    ? sqftFeature.sqftIds
    : [sqftFeature.sqftId];

  return shapeIds
    .map(shapeId => workspace.getShapeById(shapeId))
    .filter(shape => shape);
};

const SqftFeaturesPopupFeatureDropdown = (props) => {

  const {parentsList, sqftFeature, editButtonColor, clearCacheForTheEdgeFeatures, setWizardSelectedValues,
    wizardIsActive, wizardActiveItem, wizardList, storeSnapshot, wizardConflictingSteps, canOpenWizardConflict,
    sqftSlabConflicts, activeQuoteIndex} = props;
  const {feature, subPart, fromWizardGroupId} = sqftFeature;

  const [productText, setProductText] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [namesArray, setNamesArray] = useState([]);
  const [updateCount, forceUpdate] = useReducer(x => x + 1, 0);
  const dropdownElement = useRef(null);
  const inputElement = useRef(null);

  const hasSlabConflict = findSlabConflict(sqftFeature, sqftSlabConflicts);
  const hasWizardConflict = canOpenWizardConflict && wizardConflictingSteps.length && sqftFeature.fromWizardGroupId && wizardConflictingSteps.some(step => {
    return step.wizardGroupId === sqftFeature.fromWizardGroupId;
  })
  const isActiveQuote = parentsList.quoteIndex === activeQuoteIndex;
  const hasConflict = isActiveQuote && (hasWizardConflict || hasSlabConflict);

  useOutsideClick(dropdownElement, () => {
    setDropdownVisible(false);
  }, true);

  const handleInputChange = (event) => {
    setProductText(event.target.value);
  }

  const handleNavItemClick = async (navItem) => {
    navItem.part.handleClick();

    //1. Set active quote room and load sqft workspace
    if (sqftFeature.qtCatName) {
      if (!isQuoteByQTCatNameEqualToCurrentQuote(sqftFeature.qtCatName)) {
        await loadQuoteByQtCatName(sqftFeature.qtCatName).then();
      }
    }

    //2. Create snapshot before opening modal (fast - no images)
    const snapshot = createModalFixSnapshot();
    storeSnapshot(snapshot);

    //3. Update wizard selected value
    if (wizardIsActive && wizardActiveItem
      && navItem.newWizardStep !== null && navItem.newWizardStep !== undefined
      && navItem.newWizardGroup
    ) {
      const targetWizardItem = wizardList.find(wizardItem => wizardItem.id === navItem.fromWizardId) || wizardActiveItem;
      setWizardSelectedValues({
        [navItem.newWizardStep]: {
          wizardGroup: navItem.newWizardGroup,
          selectedFeature: navItem.part,
          partId: navItem.part.partId,
        }

      }, false, targetWizardItem);
    }

    // 4. Update feature in calculator
    // This will also update wizardValues (so we need to have WizardSelectedValues set before changing the feature)
    sqftShapeManager.setShapeFeature(sqftFeature.sqftId, navItem.part);
    sqftShapeManager.updateShapesWizardProperties();
    sqftShapeManager.updateShapeColorSet(sqftFeature.sqftId);

    // Slabs will be updates on feature change
    // 4.2 global sync (covers other placeholders/slabs)
    // sqftShapeIntersectionsManager.updateSlabPlaceholderFeatureId();
    // sqftShapeIntersectionsManager.updateSlabImagesFeature();

    //Detect slab conflicts
    // const { shapesWithConflict } = detectSlabConflicts();
    // const shapesIdsWithConflict = shapesWithConflict.map(shape => shape.id);
    // setSqftSlabConflicts(shapesIdsWithConflict);

    // CartItems Will be updated later when rebuilding quoteslist
    // TODO maybe we dont need to rebuild the whole lists and just update cart items...
    // 3. Update cartItem (we have to do this to correctly check getConflictingWizardSteps in setWizardSelectedValues)
    // for (const cartItem of cartItems) {
    //   if (cartItem.cartFeature.dbFeature.id === incompatibleFeatureId) {
    //     const newItem = {
    //       ...cartItem,
    //       cartFeature: currentFeature
    //     };
    //     store.dispatch(changeCartFeature(newItem));
    //   }
    // }

    // 5. update parent features feature in calculator
    if (sqftFeature.shouldUseRelatedParentFeatureAsMain) {
      for (const sqftId of sqftFeature.sqftIds) {
        sqftShapeManager.setShapeRelatedParentFeatureId(sqftId, navItem.part.dbFeature.id, navItem.part.partId);
      }
    }

    if (navItem.part.partId === SQFT_FEATURES.hinge) {
      workspaceControllers.hinge.updateResourcesId(navItem.part.dbFeature.id, navItem.part.partId)
    }

    if (navItem.part.partId.includes('handle')) {
      const shapes = workspace.getShapesWithoutAttachedResources();
      const selectedShapes = shapes.filter(shape => HANDLE_TO_CABINETS[navItem.part.partId].includes(shape.partId));
      workspaceControllers.handles.changeHandlesForSelectedShapes(selectedShapes, navItem.part, sqftFeature.quantityGroup)
    }

    // //update parentList with new navItem
    // let updatedLists = [...parentsLists];
    // let updatedList = {...parentsList};
    // const parentListIndex = parentsLists.findIndex(pList => pList === parentsList);
    // updatedList[parentKey].features[sqftFeatureIndex].feature = navItem.part;
    // if (navItem.newWizardGroupId) {
    //   // updatedList[parentKey].features[sqftFeatureIndex].fromWizardGroupId = navItem.newWizardGroupId;
    //   // we can't do that, we need to have all features, from all groups, so we can't pass direct group id here
    //   updatedList[parentKey].features[sqftFeatureIndex].newWizardGroupId = navItem.newWizardGroupId;
    //   // console.log("updated!", updatedList[parentKey].features[sqftFeatureIndex]);
    // }
    // if (isRecalculateSlabNeeded(parentsList)) {
    //   updatedList = recalculateSlabs(updatedList);
    // }
    // updatedLists[parentListIndex] = updatedList;
    // setParentsLists(updatedLists);

    // const sqftCalcData = await prepareSQFTData();
    // await saveSQFTDataToQuote(sqftCalcData);
    /** data without images */
    const coreData = prepareSQFTDataCore();
    await saveSQFTDataToQuote(coreData);

    const changedCabinetShapes = getChangedCabinetShapes(sqftFeature, navItem.part);
    if (is3DMode() && changedCabinetShapes.length) {
      await reloadShapesIn3DMode(changedCabinetShapes);
    }

    // const {addedFeatures} = computeAddedFeatures([parentsList]);
    // const tempCartItems = addUUIDToCartItems(addedFeatures);
    // console.log("tempCartItems", tempCartItems);
    // const {wizardConflictingSteps} = checkWizardConflictingSteps(tempCartItems);
    // if (wizardConflictingSteps) {
    //   setWizardConflictSteps(wizardConflictingSteps);
    //   setSQFTCartItemsToAdd(tempCartItems);
    //   //handleOpenModalFix({changeWizardStepRoute, wizardConflictingSteps, intl});
    // }
    // console.log("wizardConflictingSteps", wizardConflictingSteps);

    // wi will update parent list on quotes change
    //void rebuildQuoteLists;
    setDropdownVisible(false);
    clearCacheForTheEdgeFeatures();
  };

  const getNamesArray = () => {
    if (dropdownVisible) {
      if (!subPart.part.allFeaturesReceived) {
        return [{loader: true}]
      }
      if (wizardIsActive) {
        const passWizardGroupId = hasConflict ? null : fromWizardGroupId;
        const wizNavItems = getNavItemsFromTheWizard(
          subPart,
          "",
          {},
          passWizardGroupId,
          visualizerType,
          parentsList,
          true
        );
        return filterNavItemsWithSearch(wizNavItems, productText)
      }
      const layout = getCurrentLayout();
      const navItems = layout.designNavigationItems(subPart, {}, "", false, true);
      return filterNavItemsWithSearch(navItems, productText);
    }
    return []
  }

  useDebounce(async () => {
    if (productText && subPart.part && subPart.part.allFeaturesReceived && !subPart.part.getRealFeaturesReceivedProperty()) {
      return searchProductRequest(subPart.part, productText, setLoaderVisible)
    }
  }, [productText], 800);

  const editButtonClick = () => {
    if (dropdownVisible) {
      setDropdownVisible(false)
    } else {
      //Load feature room
      if (sqftFeature.qtCatName) {
        if (!isQuoteByQTCatNameEqualToCurrentQuote(sqftFeature.qtCatName)) {
          loadQuoteByQtCatName(sqftFeature.qtCatName).then();
        }
      }
      setDropdownVisible(true);
      if (inputElement && inputElement.current) {
        inputElement.current.focus()
      }
    }
  };

  const visualizerType = parentsList.quote ? parentsList.quote.room : document.kvConfig.visualizerType;

  useEffect(() => {
    if (loaderVisible) {
      return; // request is already going on
    }
    if (productText && subPart.part && subPart.part.allFeaturesReceived && !subPart.part.getRealFeaturesReceivedProperty()
      && !(Array.isArray(subPart.part.alreadyRequestedFeatureNames)
        && subPart.part.alreadyRequestedFeatureNames.some(alreadyRequestedText => {
          return alreadyRequestedText === productText || productText.includes(alreadyRequestedText) // arcilla red -> arcilla red suede
        }))) {
      setNamesArray([])
      searchProductRequest(subPart.part, productText, setLoaderVisible)
      return
    }
    setNamesArray(getNamesArray())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownVisible, wizardIsActive, subPart, fromWizardGroupId, visualizerType, productText, updateCount,
    loaderVisible, hasConflict]);

  const styles = {
    height: dropdownVisible
      ? loaderVisible
        ? 62
        : namesArray.length * 30 + 32
      : 0,
    width: dropdownVisible ? "100%" : 0,
  }

  return (
    <div className={"u-leading-1 u-pr-05 sqft-popup__feature-name" + (hasConflict ? " message-system-text-red" : "")}
         ref={dropdownElement}>
      <button className="sqft-popup__feature-edit" onClick={editButtonClick} >
        <EditIcon color={editButtonColor}/>
      </button>
      {feature
        ? <FeatureName feature={feature}/>
        : <FormattedMessage id="sqftFeaturesPopup.noFeatureSelected"/>}
      <div className="sqft-popup__dropdown aq-dropdown" style={styles}>
        <input className="ui-input ui-input--new ui-input--gray"
               value={productText}
               ref={inputElement}
               onChange={handleInputChange}/>
        <ul className="aq-list">
          {loaderVisible ? (
            <li className="aq-list__item bold aq-list__item--loading">
              <div className="message-loading-left-panel-spinner message-loading-left-panel-spinner--small"/>
            </li>
          ) : namesArray.map((navItem, navItemIndex) => {
            return navItem.loader ? (
              <AllFeaturesChecker subPart={subPart}
                                  key="aq-list-loader" ignored={updateCount} callBack={forceUpdate}/>
            ) : (
              <li className="aq-list__item bold" key={navItemIndex} onClick={() => handleNavItemClick(navItem)}>
                <FeatureName feature={navItem.part}/>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
};

SqftFeaturesPopupFeatureDropdown.propTypes = {
  parentsList: PropTypes.object.isRequired,
  parentsLists: PropTypes.array.isRequired,
  setParentsLists: PropTypes.func.isRequired,
  editButtonColor: PropTypes.string.isRequired,
  sqftFeature: PropTypes.object.isRequired,
}

const mapStateToProps = (store) => {
  const {wizardIsActive, wizardActiveItem, wizardList, wizardConflictingSteps} = store.wizardState;
  const {cartItems} = store.cartState;
  const {sqftSlabConflicts} = store.sqftSlabState;
  const {activeQuoteIndex} = store.quotesState;
  return {
    activeTheme: store.serverState.activeTheme,
    wizardIsActive,
    wizardActiveItem,
    wizardList,
    wizardConflictingSteps,
    canOpenWizardConflict: canOpenWizardConflictPopup(store),
    sqftSlabConflicts,
    cartItems,
    activeQuoteIndex,
  };
};

const mapDispatchToProps = {
  clearCacheForTheEdgeFeatures,
  setWizardSelectedValues,
  setWizardConflictSteps,
  setSQFTCartItemsToAdd,
  storeSnapshot,
};

export default connect(mapStateToProps, mapDispatchToProps)(SqftFeaturesPopupFeatureDropdown)
